/**
 * Zustand store: scoped `Record<field, message>` for inline validation (API + client).
 *
 * EAA-1670: first slice migrated per the Phase 7 state-migration plan — this is
 * the one Redux slice with no saga pair (purely client-side), so it proves the
 * `FieldErrorSource` seam (packages/ui) end-to-end without touching that package.
 * See docs/plans/fe-restructure/phase-7-state-migration.md.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { fieldErrorsInitialState, type FieldErrorsByScope } from '@/store/types/fieldErrors.types';

interface FieldErrorsStore {
    byScope: FieldErrorsByScope;
    setScopeFieldErrors: (scope: string, errors: Record<string, string>) => void;
    clearFieldError: (scope: string, field: string) => void;
    clearScope: (scope: string) => void;
}

export const useFieldErrorsStore = create<FieldErrorsStore>()(
    devtools(
        (set) => ({
            byScope: fieldErrorsInitialState.byScope,
            setScopeFieldErrors: (scope, errors) =>
                set(
                    (state) => {
                        if (Object.keys(errors).length === 0) {
                            const byScope = { ...state.byScope };
                            delete byScope[scope];
                            return { byScope };
                        }
                        return { byScope: { ...state.byScope, [scope]: { ...errors } } };
                    },
                    false,
                    'fieldErrors/setScopeFieldErrors'
                ),
            clearFieldError: (scope, field) =>
                set(
                    (state) => {
                        const m = state.byScope[scope];
                        if (!m || !(field in m)) {
                            return state;
                        }
                        const next = { ...m };
                        delete next[field];
                        const byScope = { ...state.byScope };
                        if (Object.keys(next).length === 0) {
                            delete byScope[scope];
                        } else {
                            byScope[scope] = next;
                        }
                        return { byScope };
                    },
                    false,
                    'fieldErrors/clearFieldError'
                ),
            clearScope: (scope) =>
                set(
                    (state) => {
                        if (!(scope in state.byScope)) {
                            return state;
                        }
                        const byScope = { ...state.byScope };
                        delete byScope[scope];
                        return { byScope };
                    },
                    false,
                    'fieldErrors/clearScope'
                ),
        }),
        { name: 'fieldErrors' }
    )
);
